import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { IKorviuInputErrorMessage, KorviuInputComponent } from 'shared/components/korviu-input/korviu-input.component';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { TranslateModule } from '@ngx-translate/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { IBankInfo } from 'shared/types/payment-account.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { KorviuSelectComponent } from 'shared/components/korviu-select/korviu-select.component';
import { AlertComponent } from 'shared/components/alert/alert.component';
import { th } from 'date-fns/locale';

enum AccountCountries {
  US = 'US',
  MX = 'MX'
}

const usAccountNumberErrorMessages: IKorviuInputErrorMessage[] = [
  {
    errorCode: 'required',
    localizedMessage: 'profile.addAccount.validations.theAccountNumberIsRequired'
  },
  {
    errorCode: 'mustContain',
    localizedMessage: 'profile.addAccount.validations.theAccountNumberMustContain'
  },
  {
    errorCode: 'invalidAccountInClabe',
    localizedMessage: 'profile.addAccount.validations.invalidAccountNumberNotInClabe'
  }
];

const mxAccountNumberErrorMessages: IKorviuInputErrorMessage[] = [
  {
    errorCode: 'required',
    localizedMessage: 'profile.addAccount.validations.theAccountNumberIsRequired'
  },
  {
    errorCode: 'mustContain',
    localizedMessage: 'profile.addAccount.validations.theAccountNumberMustContainMX'
  },
  {
    errorCode: 'invalidAccountInClabe',
    localizedMessage: 'profile.addAccount.validations.invalidAccountNumberNotInClabe'
  }
];

@Component({
  selector: 'app-bank-info',
  standalone: true,
  imports: [
    KorviuButtonComponent,
    KorviuInputComponent,
    NzRadioComponent,
    NzRadioGroupComponent,
    TranslateModule,
    FormsModule,
    TagComponent,
    NzButtonModule,
    NzIconModule,
    KorviuSelectComponent,
    ReactiveFormsModule,
    AlertComponent
  ],
  templateUrl: './bank-info.component.html',
  styleUrl: './bank-info.component.scss'
})
export class BankInfoComponent implements OnInit, AfterViewInit {
  @Input() isAdding: boolean = true;
  @Input() hideButtons: boolean = false;
  @Input() collapsable: boolean = false;
  @Input() bankInfo!: IBankInfo;
  @Input() bankInfoIsValid!: boolean;
  @Output() bankInfoChange: EventEmitter<IBankInfo> = new EventEmitter<IBankInfo>();
  @Output() bankInfoIsValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() backClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() continueClick: EventEmitter<void> = new EventEmitter<void>();

  public get editMode(): boolean {
    return this.isAdding || this.bankInfo?.status === 'DECLINED' || !this.bankInfo?.status;
  }

  public get bankNameIsDeclined(): boolean {
    return this.bankNameControl.hasError('declined');
  }
  public get accountHolderIsDeclined(): boolean {
    return this.holderFullName.hasError('declined');
  }

  public get accountNumberErrorMessages(): IKorviuInputErrorMessage[] {
    return this.accountCountryIsMX ? mxAccountNumberErrorMessages : usAccountNumberErrorMessages;
  }

  public get statusCode(): string {
    return this.bankInfo?.status;
  }

  public get bankInfoStatus(): { tagType: TagComponentType; localizableText: string } {
    if (this.statusCode) {
      if (this.statusCode === 'DECLINED') {
        return { localizableText: 'profile.declined', tagType: 'error' };
      }
      if (this.statusCode === 'REVIEWING') {
        return { localizableText: 'profile.reviewing', tagType: 'warning' };
      }
    } else {
      if (this.formIsValid) {
        return { localizableText: 'profile.submitted', tagType: 'info' };
      } else {
        return { localizableText: 'profile.addAccount.required', tagType: 'error' };
      }
    }
    return { localizableText: 'profile.approved', tagType: 'success' };
  }

  public get disabled(): boolean {
    return !this.editMode;
  }

  public currency = 2;
  public collapsed = signal<boolean>(false);
  public bankInfoForm = this._getBankInfoForm();
  public AccountCountries: typeof AccountCountries = AccountCountries;
  public mxAccountNumberErrorMessages: IKorviuInputErrorMessage[] = mxAccountNumberErrorMessages;
  public usAccountNumberErrorMessages: IKorviuInputErrorMessage[] = usAccountNumberErrorMessages;

  public get accountCountryIsUS(): boolean {
    return this.accountTypeControl?.value === AccountCountries.US;
  }
  public get accountCountryIsMX(): boolean {
    return this.accountTypeControl?.value === AccountCountries.MX;
  }
  public get formIsValid(): boolean {
    return this.bankInfoForm.valid;
  }

  public get currencyControl(): FormControl {
    return this.bankInfoForm?.get('currency') as FormControl;
  }

  public get accountTypeControl(): AbstractControl {
    return this.bankInfoForm?.get('account_type');
  }
  public get bankNameControl(): AbstractControl {
    return this.bankInfoForm?.get('bank_name');
  }
  public get accountNumberControl(): AbstractControl {
    return this.bankInfoForm?.get('account_number');
  }
  public get confirmAccountNumberControl(): AbstractControl {
    return this.bankInfoForm?.get('confirm_account_number');
  }
  public get confirmClabeControl(): AbstractControl {
    return this.bankInfoForm?.get('confirm_clabe');
  }
  public get routingNumberControl(): AbstractControl {
    return this.bankInfoForm?.get('routing_number');
  }
  public get clabeControl(): AbstractControl {
    return this.bankInfoForm?.get('clabe_number');
  }
  public get holderFullName(): AbstractControl {
    return this.bankInfoForm?.get('holder_full_name');
  }

  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.accountTypeControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value: AccountCountries) => {
        if (value === AccountCountries.US) {
          this._setUSValidators();
        } else if (value === AccountCountries.MX) {
          this._setMXValidators();
        }
        this.routingNumberControl.updateValueAndValidity();
        this.clabeControl.updateValueAndValidity();
        this.confirmClabeControl.updateValueAndValidity();
        this.accountNumberControl.updateValueAndValidity();
        this.confirmAccountNumberControl.updateValueAndValidity();
        this._cdr.detectChanges();
      });

    this.currencyControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value: number) => {
      this.currency = value;
    });
    if (this.bankInfo) {
      this.bankInfoForm.patchValue(this.bankInfo);
      this.confirmAccountNumberControl.patchValue(this.bankInfo.account_number);
      this.confirmClabeControl.patchValue(this.bankInfo.clabe_number);
      this.currency = this.bankInfo?.currency ?? 2;

      if (this.bankInfo?.bank_information?.bank_name?.status === 'DECLINED') {
        this.bankNameControl.setErrors({ declined: true });
      }
      if (this.bankInfo?.bank_information?.account_holder?.status === 'DECLINED') {
        this.holderFullName.setErrors({ declined: true });
      }
    }
    this.bankInfoForm.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value: IBankInfo) => {
      this.bankInfoChange.emit(value);
      if (this.bankInfo) {
        this.bankInfo.status = null;
      }
      this.bankInfoIsValidChange.emit(this.bankInfoForm.valid);
    });

    this.accountNumberControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.accountCountryIsUS) {
        this.confirmAccountNumberControl.updateValueAndValidity();
      }
    });

    this.clabeControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (!!this.confirmClabeControl.value) {
        this.confirmClabeControl.updateValueAndValidity();
      }
      if (!!this.accountNumberControl.value) {
        this.accountNumberControl.updateValueAndValidity();
      }
    });
  }

  public ngAfterViewInit() {
    setTimeout(() => {
      if (this.bankNameIsDeclined) {
        this.bankNameControl.markAsDirty();
      }
      if (this.accountHolderIsDeclined) {
        this.holderFullName.markAsDirty();
      }
      this.bankInfoIsValidChange.emit(this.bankInfoForm.valid);
    });
  }

  public onCurrencyChange(): void {
    this.currencyControl.patchValue(this.currency);
  }

  public onCollapseClick(): void {
    this.collapsed.set(!this.collapsed());
  }

  public onBackClick(): void {
    this.backClick.emit();
  }

  public onContinueClick(): void {
    this.continueClick.emit();
  }

  private _getBankInfoForm(): FormGroup {
    const controls: any = {
      currency: new FormControl(this.currency),
      account_type: new FormControl(AccountCountries.US),
      bank_name: new FormControl('', [Validators.required]),
      account_number: new FormControl('', [
        Validators.required,
        this._mustContainInterval(8, 17),
        this._confirmMatchesAccountValidator('account_number')
      ]),
      confirm_account_number: new FormControl('', [
        this._confirmMatchesAccountValidator('account_number'),
        this._mustContainInterval(8, 17)
      ]),
      confirm_clabe: new FormControl('', [this._confirmMatchesAccountValidator('clabe_number')]),
      routing_number: new FormControl('', [Validators.required, this._mustContain(9)]),
      clabe_number: new FormControl('', [this._mustContain(18)]),
      holder_full_name: new FormControl('', [Validators.required, Validators.minLength(3)])
    };
    return new FormGroup(controls);
  }

  private _confirmMatchesAccountValidator(accountNumberFieldName: string): ValidatorFn {
    return (confirmControl: AbstractControl): ValidationErrors | null => {
      if (!confirmControl.parent) {
        return null;
      }
      const accountNumberControl = confirmControl.parent.get(accountNumberFieldName);
      if (!accountNumberControl) {
        return null;
      }
      const account = accountNumberControl.value;
      const confirm = confirmControl.value;
      if (account && confirm && account !== confirm) {
        return { accountMismatch: true };
      }
      return null;
    };
  }

  private _setUSValidators(): void {
    this.bankInfoForm.clearValidators();
    this.currency = 2;
    this.currencyControl.setValue(2);
    this.routingNumberControl.setValidators([Validators.required, this._mustContain(9)]);
    this.clabeControl.clearValidators();
    this.clabeControl.setValue('');
    this.accountNumberControl.setValidators([
      Validators.required,
      this._mustContainInterval(8, 17),
      this._confirmMatchesAccountValidator('account_number')
    ]);
    this.confirmAccountNumberControl.setValidators([
      Validators.required,
      this._mustContainInterval(8, 17),
      this._confirmMatchesAccountValidator('account_number')
    ]);
  }

  private _setMXValidators(): void {
    this.bankInfoForm.clearValidators();

    this.routingNumberControl.clearValidators();
    this.routingNumberControl.setValue('');

    this.confirmAccountNumberControl.clearValidators();
    this.confirmAccountNumberControl.setValue('');

    this.currency = 1;
    this.currencyControl.setValue(1);
    this.clabeControl.setValidators([
      Validators.required,
      this._mustContain(18),
      this._confirmMatchesAccountValidator('clabe_number')
    ]);
    this.confirmClabeControl.setValidators([Validators.required, this._confirmMatchesAccountValidator('clabe_number')]);
    this.accountNumberControl.setValidators([
      Validators.required,
      this._mustContain(11),
      this._mxAccountNumberInClabeValidator()
    ]);
  }

  private _mxAccountNumberInClabeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.accountCountryIsMX) {
        return null;
      }
      const accountNumber: string = control.value;
      const clabe: string = this.clabeControl?.value;
      if (!accountNumber || !clabe) {
        return null;
      }
      if (!clabe.includes(accountNumber)) {
        return { invalidAccountInClabe: true };
      }

      return null;
    };
  }

  private _mustContain(length: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.length !== length) {
        return { mustContain: true };
      }
      return null;
    };
  }

  private _mustContainInterval(minLength: number, maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value && value.length >= minLength && value.length <= maxLength) {
        return null;
      }
      return { mustContain: true };
    };
  }
}
